import { useEffect, useMemo, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_QUOTES_TABLE } from "../../constants/FirestoreConstant";
import { scopeOrders } from "../../functions/permissions";
import { useAuth } from "./useAuth";
import { usePermissions } from "./usePermissions";
import { useScopedCustomers } from "./useScopedCustomers";

/**
 * Offertes (BMX-58), alleen geladen op de offertepagina's zelf — niet in de app-layout, zie
 * docs/cms-performance.md. De collectie is klein (een offerte per gesprek, niet per klik), dus de
 * lijst luistert naar alles en sorteert in de browser; `where` + `orderBy` op verschillende
 * velden zou een samengestelde index vragen die niet vanuit de repo aan te maken is.
 *
 * Zichtbaarheid zoals bij orders (`scopeOrders`: land of klant), plus altijd de offertes die je
 * zelf gemaakt hebt — een offerte voor een nieuwe klant heeft nog geen klant-id.
 */
export const useScopedQuotes = () => {
  const firestore = useFirestore();
  const { uid } = useAuth();
  const { scope } = usePermissions();
  const customers = useScopedCustomers();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = firestore.collection(FIRESTORE_QUOTES_TABLE).onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setQuotes(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Offertes ophalen mislukt:", err);
        setError(err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [firestore]);

  const scoped = useMemo(() => {
    const visible = new Set((scopeOrders(quotes, scope, customers) || []).map((quote) => quote.id));
    return quotes.filter((quote) => visible.has(quote.id) || (!!uid && quote.createdBy?.uid === uid));
  }, [quotes, scope, customers, uid]);

  return { quotes: scoped, loading, error };
};

/** Eén offerte, live — zodat een wijziging uit de webshop of een verzending meteen zichtbaar is. */
export const useQuote = (quoteId) => {
  const firestore = useFirestore();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!quoteId) return undefined;
    setLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_QUOTES_TABLE)
      .doc(quoteId)
      .onSnapshot(
        (doc) => {
          setQuote(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Offerte ophalen mislukt:", err);
          setError(err);
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, quoteId]);

  return { quote, loading, error };
};
